'use strict';
(() => {
  const id = value => Number.isSafeInteger(Number(value)) && Number(value) > 0;
  const titleKey = value => String(value || '').trim().toLocaleLowerCase().replace(/\s+/g, ' ');
  const autumn = {
    coat: ['knit', 'bottom'], knit: ['bottom', 'coat'], top: ['bottom', 'knit'],
    bottom: ['knit', 'top'], dress: ['knit', 'boots'], shoes: ['bottom', 'knit'],
    boots: ['bottom', 'knit'], sneakers: ['bottom', 'knit'],
  };
  const mild = {
    coat: ['top', 'bottom'], knit: ['bottom', 'top'], top: ['bottom', 'sneakers'],
    bottom: ['top', 'sneakers'], dress: ['sneakers', 'knit'], shoes: ['bottom', 'top'],
    boots: ['bottom', 'top'], sneakers: ['bottom', 'top'],
  };
  function classify(context, groups) {
    if (!id(context?.id) || context.giftCard || context.published !== true || !Array.isArray(context.collections)) return null;
    const handles = new Set(context.collections);
    const matched = groups.filter(group => handles.has(group.handle));
    const genders = new Set(matched.map(group => group.gender));
    for (const gender of ['dames', 'heren']) if (handles.has('olv-' + gender)) genders.add(gender);
    if (genders.size !== 1 || !['dames', 'heren'].includes([...genders][0])) return null;
    const categories = new Set(matched.map(group => group.category));
    if (categories.has('boots') || categories.has('sneakers')) categories.delete('shoes');
    if (categories.size !== 1) return null;
    return { id: Number(context.id), gender: [...genders][0], category: [...categories][0] };
  }
  function plan(contexts, pool, cart, groups, month) {
    if (!Array.isArray(cart?.items) || !cart.items.length || cart.items.length > 8) return [];
    const inCart = new Set(cart.items.map(item => Number(item.product_id)));
    const names = new Set(cart.items.map(item => titleKey(item.product_title || item.title)));
    const classified = contexts.map(context => classify(context, groups));
    if (contexts.length !== cart.items.length || classified.some(context => !context || !inCart.has(context.id))) return [];
    const genders = new Set(classified.map(context => context.gender));
    if (genders.size !== 1) return [];
    const gender = [...genders][0], present = new Set(classified.map(context => context.category));
    const rules = Number(month) >= 9 || Number(month) <= 2 ? autumn : mild;
    const targets = [...new Set([...classified].reverse().flatMap(context => rules[context.category] || []))].filter(category => !present.has(category));
    const seen = new Set(inCart), chosen = [];
    for (const category of targets) {
      const entry = pool.find(candidate => candidate.gender === gender && candidate.category === category && candidate.published === true && candidate.available === true && !candidate.giftCard && id(candidate.id) && candidate.price > 0 && !seen.has(Number(candidate.id)) && !names.has(titleKey(candidate.title)));
      if (!entry) continue;
      chosen.push(entry); seen.add(Number(entry.id)); names.add(titleKey(entry.title));
      if (chosen.length === 2) break;
    }
    return chosen;
  }
  function eligible(product, entry) {
    return Boolean(product && entry?.published === true && !entry.giftCard && Number(product.id) === Number(entry.id) && product.handle === entry.handle && product.available === true && Number.isSafeInteger(product.price) && product.price > 0 && product.published_at !== null && !product.gift_card && !product.requires_selling_plan && !/gift\s*card|cadeaukaart/i.test(product.type || '') && Array.isArray(product.variants) && product.variants.some(variant => variant.available && Number.isSafeInteger(variant.price) && variant.price > 0));
  }
  function defaultOnly(product) {
    return product.options?.length === 1 && /^title$/i.test(product.options[0].name) && product.options[0].values?.length === 1 && /^default title$/i.test(product.options[0].values[0]);
  }
  function initialChoices(product) {
    return (product.options || []).map(option => defaultOnly(product) ? String(option.values[0]) : null);
  }
  function selectedVariant(product, choices) {
    if (!Array.isArray(product?.options) || !Array.isArray(choices) || choices.length !== product.options.length || choices.some(value => typeof value !== 'string')) return null;
    return product.variants.find(variant => variant.options?.length === choices.length && variant.options.every((value, index) => String(value) === choices[index])) || null;
  }
  function identity(item) {
    const properties = (Array.isArray(item.properties) ? [...item.properties] : Object.entries(item.properties || {})).sort(([a], [b]) => String(a).localeCompare(String(b)));
    return JSON.stringify([Number(item.variant_id), properties, item.selling_plan_allocation?.selling_plan?.id ?? null]);
  }
  function quantities(items) {
    const result = new Map();
    for (const item of items || []) {
      if (!id(item.variant_id) || !Number.isSafeInteger(item.quantity) || item.quantity < 1) return null;
      const key = identity(item); result.set(key, (result.get(key) || 0) + item.quantity);
    }
    return result;
  }
  function additionVerified(before, after, productId, variantId) {
    if (!Array.isArray(before?.items) || !Array.isArray(after?.items) || before.items.some(item => Number(item.product_id) === Number(productId)) || after.item_count !== before.item_count + 1) return false;
    const added = after.items.filter(item => Number(item.product_id) === Number(productId));
    if (added.length !== 1 || Number(added[0].variant_id) !== Number(variantId) || added[0].quantity !== 1 || added[0].selling_plan_allocation || Object.keys(added[0].properties || {}).length) return false;
    const expected = quantities(before.items), actual = quantities(after.items.filter(item => Number(item.product_id) !== Number(productId)));
    return Boolean(expected && actual && expected.size === actual.size && [...expected].every(([key, quantity]) => actual.get(key) === quantity));
  }
  async function performAdd(state, services) {
    const before = await services.readCart();
    if (before.items.some(item => Number(item.product_id) === Number(state.entry.id))) return { status: 'already', cart: before };
    if (typeof services.allowed !== 'function' || !await services.allowed(state.entry, before)) return { status: 'changed', cart: before };
    const fresh = await services.readProduct(state.entry.handle);
    if (!eligible(fresh, state.entry)) return { status: 'unavailable', cart: before, product: fresh };
    const selected = selectedVariant(fresh, state.choices);
    if (!selected?.available || selected.requires_selling_plan || !Number.isSafeInteger(selected.price) || selected.price <= 0 || !id(selected.id)) return { status: 'unavailable', cart: before, product: fresh };
    const displayed = selectedVariant(state.product, state.choices);
    if (!displayed || displayed.price !== selected.price) return { status: 'price_changed', cart: before, product: fresh };
    let rejected = false;
    try { await services.postAdd(selected.id); }
    catch (error) { rejected = error.confirmedRejection === true; }
    let after;
    try { after = await services.readCart(); }
    catch (_) { return { status: 'uncertain' }; }
    if (additionVerified(before, after, fresh.id, selected.id)) return { status: 'confirmed', cart: after };
    return { status: rejected ? 'rejected' : 'uncertain', cart: after };
  }
  const core = { classify, plan, eligible, defaultOnly, initialChoices, selectedVariant, additionVerified, performAdd };
  if (typeof module !== 'undefined') module.exports = core;
  if (typeof window !== 'undefined') window.OluvieCartUpsellCore = core;
})();
